import {minBy, sumBy} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {usePrevious} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import type {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useSearchInConversation} from '../../../../../../srcCommon/inboxes/search/useSearchInConversation';
import type {SearchInConversationMatchModel} from '../../../../../models/api/searchInConversationMatchModel';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {trackSearchInConversationJumpToMatch} from '../../../search/searchTrackingHelpers';
import {useHighlightedMatches} from './useHighlightedMatches';

type UseConversationSearchContextActivitiesProps = {
  panelId: ViewerPanelId;
  page: ViewerPanelConversationPageState;
};

type ChangeFocusedIndex =
  | {jumpTo: SearchInConversationMatchModel; jumpDirection?: 'next' | 'previous'}
  | {jumpTo?: SearchInConversationMatchModel; jumpDirection: 'next' | 'previous'};

export const useConversationSearchContextActivities = ({
  panelId,
  page,
}: UseConversationSearchContextActivitiesProps) => {
  const [query, setQuery] = useState<string | undefined>();
  const {
    matchesInConversation: results,
    refetchActivityIds,
    isLoading,
  } = useSearchInConversation(query, page.conversationId);
  const [matchingActivity, setMatchingActivity] = useState<SearchInConversationMatchModel | null>(null);

  // This is the main function that handles the navigation between results.
  const changeFocusedIndex = useCallback(
    ({
      // To add one or subtract one from the current index.
      // Jump to a specific index.
      jumpTo,
      jumpDirection,
    }: ChangeFocusedIndex) => {
      if (!results) {
        return;
      }
      setMatchingActivity((currentMatched) => {
        if (jumpTo) {
          return jumpTo;
        }
        const currentIndex = results.findIndex((result) => result.id === currentMatched?.id);
        const increment = jumpDirection === 'next' ? 1 : -1;
        if (currentIndex === -1 || !increment) {
          return results[0];
        }
        let newIndex = currentIndex + increment;
        const resultsLength = results.length;
        const isMinimum = newIndex < 0;
        const isMaximum = newIndex >= resultsLength;
        newIndex = isMinimum ? resultsLength - 1 : newIndex;
        newIndex = isMaximum ? 0 : newIndex;
        const newMatch = results[newIndex];
        if (query && jumpDirection) {
          trackSearchInConversationJumpToMatch({
            query,
            jumpDirection,
            activityId: newMatch.id,
          });
        }
        return newMatch;
      });
    },
    [setMatchingActivity, results, query],
  );

  const previousQuery = usePrevious(query);
  // When the query changes, we refetch the results.
  useEffect(() => {
    if (!query || query === previousQuery) {
      return;
    }
    refetchActivityIds();
  }, [query, previousQuery, refetchActivityIds]);

  const onFetchingDone = useCallback(
    (newMatches: ReadonlyArray<SearchInConversationMatchModel>) => {
      if (!newMatches || newMatches.length === 0 || Object.keys(page.activities).length === 0) {
        return;
      }
      // If the currently matching activity is part of the results, we stay on it.
      const oldMatchOnPage = matchingActivity
        ? newMatches.find((match) => match.id === matchingActivity.id)
        : null;
      if (oldMatchOnPage) {
        changeFocusedIndex({jumpTo: oldMatchOnPage});
        return;
      }

      // If the currently matching activity is not part of the results, we try to find the closest one.
      // This is a list of all the activities that are on the page and are part of the results.
      const matchingActivitiesOnPage = newMatches.filter((match) => page.activities[match.id]);
      if (matchingActivitiesOnPage.length > 0 && matchingActivity) {
        const closestId = minBy(matchingActivitiesOnPage, (match) =>
          Math.abs(match.id - matchingActivity.id),
        );
        // Jump to the closest matching activity.
        changeFocusedIndex({
          // If there is no closest activity, we jump to the first one.
          jumpTo: closestId ?? newMatches[newMatches.length - 1],
        });
        return;
      }

      // Jump to the last result.
      changeFocusedIndex({
        jumpTo: newMatches[newMatches.length - 1],
      });
    },
    [matchingActivity, changeFocusedIndex, page],
  );

  useEffect(() => {
    onFetchingDone(results);
  }, [onFetchingDone, results]);

  const onElementsEnd = useCallback(
    (dir: 'start' | 'end') => {
      if (dir === 'start') {
        changeFocusedIndex({jumpDirection: 'previous'});
      } else {
        changeFocusedIndex({jumpDirection: 'next'});
      }
    },
    [changeFocusedIndex],
  );

  const {onResults, goToPreviousMatch, goToNextMatch, matchIndex} = useHighlightedMatches({
    panelId,
    matchingActivityId: matchingActivity?.id,
    onElementsEnd,
  });

  const focusedResultIndex = useMemo(() => {
    if (!matchingActivity) {
      return -1;
    }
    const matchingActivityIndex = results.findIndex((r) => r.id === matchingActivity.id);
    return sumBy(results.slice(0, matchingActivityIndex), 'numberOfMatches') + matchIndex;
  }, [matchingActivity, results, matchIndex]);

  const contextValue = useMemo(() => {
    let matchesCount = sumBy(results ?? [], 'numberOfMatches');
    if (isLoading) {
      matchesCount = -2;
    }
    return {
      query,
      setQuery,
      resetQuery: () => setQuery(undefined),
      results,
      onResults,
      focusedResultIndex,
      goToPrevious: () => goToPreviousMatch(results),
      goToNext: goToNextMatch,
      matchesCount,
    };
  }, [focusedResultIndex, goToNextMatch, goToPreviousMatch, onResults, query, results, isLoading]);
  return contextValue;
};
